import {
  Button,
  Grid,
  Box,
  IconButton,
  Stack,
  Typography,
  Radio,
  TextField as MuiTextField,
  Dialog
} from '@mui/material';
import { useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, request, toast } from '../../../../../utils';
import { TRANSACTION_TYPE_BLOOD_BANK } from '../../../../../constants/dropdown';
import { DonorDetailsForm } from './DonorDetailsForm';

const TABLE_HEADER = [
  {
    header: 'Component Name',
    accessor: (row) => row?.component
  }
];

export const BloodRequestDetailsForm = ({
  onClose,
  onAdd,
  bloodGroupDropdown,
  componentDropdown,
  bloodGroupsId,
  componentsId,
  setBloodIntakeData,
  bloodIntakeData,
  donoruseCredit,
  setDonorUseCredit,
  rowSelected
}) => {
  const [input, setInput] = useState('');
  const [openDonorDetailDialogue, setOpenDonorDetailDialogue] = useState(false);
  const [isDonor, setIsDonor] = useState(false);
  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        component: Yup.string().required('Please select Doctor'),
        blood_group: Yup.string().required('Please select Service')
      })
    ),
    defaultValues: {
      quantity: 1,
      rate: 0,
      amount: 0,
      donor_disc: 0,
      component: componentsId,
      blood_group: bloodGroupsId
    },
    mode: 'all'
  });
  const { reset } = methods;

  const { handleSubmit, setValue, watch } = methods;

  const formSubmitHandler = (formValues) => {
    if (formValues.stock_available >= formValues?.quantity) {
      formValues.gst_per = +formValues.gst_per;
      formValues.disc_per = +formValues.disc_per;
      formValues.quantity = +formValues.quantity;
      const transformedValues = {
        ...formValues
      };
      onAdd(transformedValues);
    } else {
      toast('Insufficient Stock', { variant: 'warning' });
    }
  };

  const rateWatch = watch('rate');
  const quantityWatch = watch('quantity');
  const discAmtWatch = watch('donor_disc');

  useEffect(() => {
    const amount = rateWatch * quantityWatch - discAmtWatch;
    setValue('amount', amount);
  }, [rateWatch, quantityWatch, setValue, discAmtWatch]);

  useEffect(() => {
    if (rateWatch && quantityWatch && discAmtWatch !== undefined) {
      const amount = rateWatch * quantityWatch;
      const newDiscPer = parseFloat(((discAmtWatch / amount) * 100).toFixed(6));
      setValue('disc_per', newDiscPer);
    }
  }, [rateWatch, quantityWatch, discAmtWatch, setValue]);

  const componentId = watch('component');
  const bloodGroupId = watch('blood_group');

  const batchNo = watch('batch_no');
  const serialNo = watch('serial_no');
  const component = watch('component');
  const bloodgroup = watch('blood_group');


  const handleSearchQuantity = async () => {
    const donor = bloodIntakeData?.find((donor) => donor.serialNo === serialNo);
    if (donor) {
      toast('This Product is Already Added or Sold', { variant: 'warning' });
    } else {
      try {
        if (batchNo && serialNo && component && bloodgroup) {
          const response = await request('/blood-intake-expiry-date-price-list', {
            method: 'GET',
            query: { batchNo, serialNo, component, bloodgroup }
          });
          setValue('stock_available', response?.data.sold_details.quantity);
          const expDate = new Date(response?.data?.exp_date);
          const formattedExpDate = expDate.toISOString().slice(0, 7);
          setValue('exp_date', formattedExpDate);
          setValue('rate', response?.data?.sold_details?.rate);
        }
      } catch (error) {
        console.error('Error during search:', error);
        toast('This Serial No is Not Exist in the Stock', { variant: 'warning' });
      }
    }
  };

  useEffect(() => {
    handleSearchQuantity();
  }, [input]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const transactionType = watch('transaction_type');
  useEffect(() => {
    if (transactionType === 'donor') {
      setIsDonor(true);
      setOpenDonorDetailDialogue(true);
    } else {
      setValue('donor_disc', 0);
    }
  }, [transactionType]);

  useEffect(() => {
    if (!isDonor) {
      setValue('transaction_type', 'cash');
    }
  }, [isDonor]);

  const onAddDetailHandler = (data) => {
    setOpenDonorDetailDialogue(false);
    setValue('donor_disc', rateWatch);
    setValue('donorId', data?.donorId);

    const donorusedCredit = {
      donorId: data?.donorId,
      credit: data?.total_credit - 1
    };

    setDonorUseCredit((prevState) => {
      const existingDonorIndex = prevState.findIndex((donor) => donor.donorId === donorusedCredit.donorId);

      if (existingDonorIndex !== -1) {
        // If donor is found, update the credit
        const updatedDonors = [...prevState];
        updatedDonors[existingDonorIndex] = {
          ...updatedDonors[existingDonorIndex],
          credit: donorusedCredit.credit
        };
        return updatedDonors;
      } else {
        // If donor is not found, add the new donor
        return [...prevState, donorusedCredit];
      }
    });
  };

  return (
    <>
      <Stack spacing={1} sx={{ height: '100%' }}>
        <Stack direction="row" spacing={0} alignItems="center" justifyContent="space-between" borderColor="grey.300">
          <Typography variant="h4" fontWeight="bold">
            Add Blood Intake Details
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <FormProvider
          sx={{ height: '100%', overflow: 'auto' }}
          methods={methods}
          onSubmit={handleSubmit(formSubmitHandler)}
        >
          <Grid container spacing={1} sx={{ flex: 1, height: '100%', overflow: 'auto' }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, px: 2 }}>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Component Name</Typography>
                  <SelectTag
                   disabled={rowSelected}
                    menus={componentDropdown.map((data) => ({ ...data, name: data?.component }))}
                    formControlSX={{ flex: 0.8 }}
                    name="component"
                    placeholder="Enter Component Name"
                  />
                </Stack>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Blood Group</Typography>
                  <SelectTag
                    menus={bloodGroupDropdown.map((data) => ({ ...data, name: data?.group }))}
                    formControlSX={{ flex: 0.8 }}
                    disabled={rowSelected}
                    name="blood_group"
                    placeholder="Select Blood Group"
                  />
                </Stack>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Batch No</Typography>
                  <TextField sx={{ flex: 0.8 }} name="batch_no" placeholder="Enter Batch No" />
                </Stack>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Serial No</Typography>
                  <TextField
                    sx={{ flex: 0.8 }}
                    name="serial_no"
                    placeholder="Enter Serial No"
                    onChange={(e) => handleInputChange(e)}
                    onBlur={(e) => {
                      handleInputChange(e);
                    }}
                  />
                </Stack>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Transaction Type</Typography>
                  <SelectTag
                    menus={TRANSACTION_TYPE_BLOOD_BANK}
                    formControlSX={{ flex: 0.8 }}
                    name="transaction_type"
                    placeholder="Select Transaction Type"
                  />
                </Stack>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Exp Date</Typography>
                  <TextField sx={{ flex: 0.8 }} name="exp_date" type="month" disabled />
                </Stack>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Stock Available</Typography>
                  <TextField sx={{ flex: 0.8 }} name="stock_available" placeholder="Total Stock Available" disabled />
                </Stack>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Quantity</Typography>
                  <TextField
                    sx={{ flex: 0.3 }}
                    type="number"
                    disabled
                    inputProps={{ min: '0' }}
                    name="quantity"
                    placeholder="Quantity"
                  />
                  <Typography sx={{ flex: 0.1, fontWeight: 700, fontSize: '1.4rem' }}>Rate</Typography>
                  <TextField sx={{ flex: 0.35 }} name="rate" placeholder="Rate" disabled />
                </Stack>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Donor Discount</Typography>
                  <TextField sx={{ flex: 0.8 }} name="donor_disc" disabled placeholder="Donor Discount Amount" />
                </Stack>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Amount</Typography>
                  <TextField sx={{ flex: 0.8 }} name="amount" disabled placeholder="amount" />
                </Stack>
                <Stack
                  spacing={1}
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ marginTop: '2%' }}
                >
                  <Button variant="contained" type="submit">
                    Add
                  </Button>
                  <Button variant="outlined" onClick={onClose} color="error">
                    Close
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </FormProvider>
      </Stack>

      <Dialog
        open={openDonorDetailDialogue}
        onClose={() => {
          setOpenDonorDetailDialogue(false);
          setIsDonor(false);
        }}
        PaperProps={{ sx: { width: '70%', p: 2, height: '75vh' } }}
        maxWidth="xl"
        scroll="body"
      >
        <DonorDetailsForm
          onClose={() => {
            setOpenDonorDetailDialogue(false);
            setIsDonor(false);
          }}
          componentDropdown={componentDropdown}
          bloodGroupDropdown={bloodGroupDropdown}
          isDonor={isDonor}
          setIsDonor={setIsDonor}
          onAdd={onAddDetailHandler}
          donoruseCredit={donoruseCredit}
        />
      </Dialog>
    </>
  );
};
